import React from 'react'
import NextImage from 'next/image'
import NextLink from 'next/link'
import { useRouter } from 'next/router'

import { Avatar } from '@wartek-id/avatar'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import { SocialShareDrawer } from 'components/SocialShareDrawer'
import { IS_PROFILE_REVAMP_PHASE_TWO_ENABLED } from 'configs/toggle'
import { SHARE_TEXT } from 'app/ProfileRevampPage/constants'
import { ContactDrawer } from 'app/ProfileRevampPage/components/ContactDrawer'
import { EditProfileDrawer } from 'app/ProfileRevampPage/components/EditProfileDrawer'
import { EditPersonalDataDrawer } from 'app/ProfileRevampPage/components/EditPersonalDataDrawer'
import { Subjects } from 'app/ProfileRevampPage/components/Subjects'
import { useProfile } from 'app/ProfileRevampPage/hooks/useProfile'
import { useShare } from 'app/ProfileRevampPage/hooks/useShare'
import { useSETrackEvent } from 'app/ProfileRevampPage/hooks/useSETrackEvent'
import { useSETrackEventEditProfile } from 'app/ProfileRevampPage/hooks/useSETrackEventEditProfile'
import { useAuth } from 'utils/auth'

import type { ReactElement } from 'react'

type TEditPhotoLink = {
  children: ReactElement
  href: string
  pageElement: 'PROFILE_PICTURE' | 'HEADER_PICTURE'
}
const EditPhotoLink = ({
  children,
  href,
  pageElement,
  ...props
}: TEditPhotoLink) => {
  const { isCurrentUser } = useProfile()
  const { SETrackClickProfileItem } = useSETrackEventEditProfile()

  return IS_PROFILE_REVAMP_PHASE_TWO_ENABLED && isCurrentUser ? (
    <NextLink href={href} passHref>
      <a
        {...props}
        className="cursor-pointer"
        onClick={() =>
          SETrackClickProfileItem({
            pageElement,
          })
        }
      >
        {children}
      </a>
    </NextLink>
  ) : (
    <div {...props}>{children}</div>
  )
}

const ProfileHeader = React.forwardRef<HTMLDivElement, {}>(({}, ref) => {
  const { session } = useAuth()
  const { user = null } = session || {}
  const isLoggedIn = Boolean(session) && Boolean(user)
  const { SETrackClickContactButton, SETrackClickShareProfile } =
    useSETrackEvent()
  const {
    SETrackClickEditProfileButton,
    SETrackOpenEditProfilePopup,
    SETrackCloseEditProfilePopup,
    SETrackOpenPersonalDataPopup,
    SETrackClosePersonalDataPopup,
  } = useSETrackEventEditProfile()

  const router = useRouter()

  const { isCurrentUser, isNarasumber, profileData } = useProfile()
  const {
    shareUrl,
    socialShareDrawerOpen,
    setSocialShareDrawerOpen,
    onClickShare,
  } = useShare()
  const bannerImage =
    IS_PROFILE_REVAMP_PHASE_TWO_ENABLED && Boolean(profileData?.bannerImage)
      ? profileData?.bannerImage
      : '/images/profile-default-header.svg'

  const [contactDrawerOpen, setContactDrawerOpen] =
    React.useState<boolean>(false)
  const [editProfileDrawerOpen, setEditProfileDrawerOpen] =
    React.useState<boolean>(false)
  const [editPersonalDataDrawerOpen, setEditPersonalDataDrawerOpen] =
    React.useState<boolean>(false)

  const onClickContact = () => {
    // force login when visitor intended to "Hubungi Guru"
    SETrackClickContactButton()

    if (!isLoggedIn) {
      return router.push({
        pathname: '/login',
        query: {
          from: router.asPath,
        },
      })
    }

    setContactDrawerOpen(true)
  }

  const onContactDrawerClosed = () => {
    setContactDrawerOpen(false)
  }

  const onClickShareButton = () => {
    onClickShare()
    SETrackClickShareProfile({
      pageElement: 'button',
    })
  }

  const onClickEditProfile = () => {
    setEditProfileDrawerOpen(true)
    SETrackClickEditProfileButton()
  }

  const onEditProfileCloseButtonClicked = () => {
    setEditProfileDrawerOpen(false)
    SETrackCloseEditProfilePopup()
  }

  const onEditPersonalDataCloseButtonClicked = () => {
    setEditPersonalDataDrawerOpen(false)
    SETrackClosePersonalDataPopup()
  }

  React.useEffect(() => {
    if (editProfileDrawerOpen) {
      SETrackOpenEditProfilePopup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProfileDrawerOpen])

  React.useEffect(() => {
    if (editPersonalDataDrawerOpen) {
      SETrackOpenPersonalDataPopup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPersonalDataDrawerOpen])

  return (
    <div ref={ref}>
      <EditPhotoLink
        data-testid="profile-header-cover"
        href="/profil/edit/cover"
        pageElement="HEADER_PICTURE"
      >
        <NextImage
          layout="responsive"
          src={bannerImage}
          alt="Cover Image"
          width="720px"
          height="240px"
          priority
        />
      </EditPhotoLink>
      <div data-testid="profile-header-content" className="px-4 pb-4 -mt-10">
        <EditPhotoLink
          data-testid="profile-header-avatar"
          href="/profil/edit/photo"
          pageElement="PROFILE_PICTURE"
        >
          <Avatar
            src={profileData?.image}
            name={profileData?.name}
            size="xxl"
            alt="Avatar"
            className="!w-[80px] !h-[80px] border border-white !shadow-none mb-1"
          />
        </EditPhotoLink>

        <div className="mb-1">
          <Text variant="heading-lg" className="break-words">
            {profileData?.name}
          </Text>
        </div>
        {isNarasumber && (
          <div className="flex space-x-1 items-center">
            <NextImage
              layout="fixed"
              src="/icon/star.png"
              alt="Star"
              width="16px"
              height="16px"
            />
            <Text variant="body-sm">Narasumber Praktik Baik IKM</Text>
          </div>
        )}
        {Boolean(profileData?.school) && (
          <Text variant="body-sm" className="break-words">
            {profileData.school}
          </Text>
        )}
        {Boolean(profileData?.jenisPtk) && (
          <Text variant="body-sm" className="break-words">
            {profileData.jenisPtk}
          </Text>
        )}
        {IS_PROFILE_REVAMP_PHASE_TWO_ENABLED &&
        (profileData?.subjects ?? []).length > 0 ? (
          <Subjects />
        ) : null}

        <div className="mt-3 grid grid-cols-12 gap-x-4">
          {IS_PROFILE_REVAMP_PHASE_TWO_ENABLED && isCurrentUser && (
            <div className="col-span-6">
              <Button
                data-testid="profile-header-edit-button"
                color="white"
                className="space-x-1 md:space-x-2"
                fullWidth
                onClick={onClickEditProfile}
              >
                <Text variant="action">Edit profil</Text>
                <Icon fontSize="small">arrow_drop_down</Icon>
              </Button>
            </div>
          )}

          {isNarasumber && !isCurrentUser && (
            <div className="col-span-6">
              <Button
                data-testid="profile-header-contact-button"
                color="white"
                className="space-x-1 md:space-x-2"
                fullWidth
                onClick={onClickContact}
              >
                <Text variant="action">Hubungi guru</Text>
              </Button>
            </div>
          )}

          <div className="col-span-6">
            <Button
              data-testid="profile-header-share-button"
              color="white"
              className="space-x-1 md:space-x-2"
              fullWidth
              onClick={onClickShareButton}
            >
              <Icon fontSize="small">share</Icon>
              <Text variant="action">Bagikan profil</Text>
            </Button>
          </div>
        </div>
      </div>

      {socialShareDrawerOpen && (
        <SocialShareDrawer
          isOpen={true}
          onClose={() => {
            setSocialShareDrawerOpen(false)
          }}
          shareUrl={shareUrl}
          shareText={SHARE_TEXT}
        />
      )}

      {contactDrawerOpen && (
        <ContactDrawer
          isOpen={true}
          onClose={onContactDrawerClosed}
          onShare={() => {
            onClickShare()
          }}
        />
      )}

      {editProfileDrawerOpen && (
        <EditProfileDrawer
          isOpen={true}
          onClose={() => setEditProfileDrawerOpen(false)}
          onEditPersonalDataClicked={() => {
            setEditProfileDrawerOpen(false)
            setEditPersonalDataDrawerOpen(true)
          }}
          onCloseButtonClicked={onEditProfileCloseButtonClicked}
        />
      )}

      {editPersonalDataDrawerOpen && (
        <EditPersonalDataDrawer
          isOpen={true}
          onClose={() => setEditPersonalDataDrawerOpen(false)}
          onCloseButtonClicked={onEditPersonalDataCloseButtonClicked}
        />
      )}
    </div>
  )
})
ProfileHeader.displayName = 'ProfileHeader'

export default ProfileHeader
